// Super-admin login page — POSTs to admin-api /auth/login.
// admin-api sets the auth as an httpOnly cookie; we never see the JWT here.
// The main HCM API bearer is held in sessionStorage via setAccessToken — see
// apps/admin/src/lib/api.ts for the rationale.
//
// admin-api enforces TOTP for super_admins with totp_enabled=true; this form
// sends totp_token (or recovery_code) on the first request. Errors from any
// step (admin-api login, /me lookup, main-API login for the bearer) are now
// surfaced inline — none are silently swallowed, and no navigate happens
// until every step has succeeded.

import { useState } from "react";
import type { JSX, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { adminFetch, apiFetch, setAccessToken, ApiError } from "../lib/api.js";

interface LoginResponse {
  // admin-api still returns this for non-browser clients, but the browser
  // ignores it: auth flows entirely through the admin_session httpOnly cookie.
  access_token?: string;
}

function describeApiError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; mfa_required?: boolean; mfa_setup_required?: boolean } | null;
    if (body?.error) return body.error;
    if (err.status === 401) return "Invalid credentials or MFA token.";
    if (err.status === 403) return "Access denied.";
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

// eslint-disable-next-line max-lines-per-function -- login + TOTP + lockout flow; cohesive single page
export function AdminLoginPage(): JSX.Element {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [showRecovery, setShowRecovery] = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [info,     setInfo]     = useState<string | null>(null);
  const [loading,  setLoading]  = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const navigate = useNavigate();

  async function handleForgotPassword(): Promise<void> {
    setError(null);
    setInfo(null);
    if (!email.trim()) {
      setError("Enter your email above first.");
      return;
    }
    setResetSending(true);
    try {
      await apiFetch("/hcm/api/v1/auth/forgot-password", {
        method: "POST",
        body:   JSON.stringify({ email: email.trim() }),
      });
      setInfo("Reset link sent to your email if an account exists.");
    } catch (err) {
      if (err instanceof ApiError) {
        setError("Could not send reset link. Try again in a few minutes.");
      } else {
        setError("Network error. Check your connection.");
      }
    } finally {
      setResetSending(false);
    }
  }

  // eslint-disable-next-line complexity, sonarjs/cognitive-complexity -- branches across totp, recovery, and lockout error paths
  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // Build the admin-api login payload. Send totp_token OR recovery_code
      // (mutually exclusive — admin-api loginSchema rejects both).
      const loginBody: {
        email: string; password: string;
        totp_token?: string; recovery_code?: string;
      } = { email: email.trim(), password };
      if (showRecovery) {
        if (recoveryCode.trim()) loginBody.recovery_code = recoveryCode.trim();
      } else {
        if (totpCode.trim()) loginBody.totp_token = totpCode.trim();
      }

      // Step 1 — admin-api login (sets admin_session cookie). Errors propagate
      // to the outer catch and surface as inline errors; we stay on /login.
      await adminFetch<LoginResponse>("/auth/login", {
        method: "POST",
        body:   JSON.stringify(loginBody),
      });

      // Step 2 — read the user's profile to decide the bootstrap branch.
      // Failure here means our session cookie didn't authenticate; surface it.
      let totpEnabled = true;
      try {
        const me = await adminFetch<{ totp_enabled?: boolean }>("/admin/users/me");
        totpEnabled = Boolean(me.totp_enabled);
      } catch (err) {
        setError(describeApiError(err, "Could not verify your account. Try signing in again."));
        return;
      }

      if (!totpEnabled) {
        // Bootstrap path — TotpSetupPage hits the main HCM API directly to
        // enable TOTP; admin-api allows the cookie through /admin/users/me
        // and blocks every other route until totp_enabled flips to true.
        navigate(`/setup-totp?email=${encodeURIComponent(email.trim())}`, { replace: true });
        return;
      }

      // Step 3 — main HCM API login to mint a bearer for /api/super-admin/*.
      // We forward the same totp_token / recovery_code; failure is surfaced.
      try {
        const hcmBody: { email: string; password: string; totp_token?: string; recovery_code?: string }
          = { email: email.trim(), password };
        if (showRecovery && recoveryCode.trim()) hcmBody.recovery_code = recoveryCode.trim();
        else if (!showRecovery && totpCode.trim()) hcmBody.totp_token = totpCode.trim();

        const hcmRes = await apiFetch<LoginResponse>("/hcm/api/v1/auth/login", {
          method: "POST",
          body: JSON.stringify(hcmBody),
        });
        if (hcmRes.access_token) setAccessToken(hcmRes.access_token);
      } catch (err) {
        setError(describeApiError(err, "MFA verification failed on main API."));
        return;
      }

      navigate("/tenants", { replace: true });
    } catch (err) {
      setError(describeApiError(err, "Login failed."));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-md w-full max-w-sm p-8 space-y-6">
        <div className="text-center">
          <span className="text-2xl font-bold text-indigo-700">HCM Admin</span>
          <p className="mt-1 text-sm text-slate-500">Super-admin access only</p>
        </div>

        <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="admin@example.com"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="••••••••"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {!showRecovery && (
            <div>
              <label htmlFor="totp" className="block text-sm font-medium text-slate-700 mb-1">
                6-digit TOTP code
              </label>
              <input
                id="totp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                autoComplete="one-time-code"
                placeholder="123456"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                type="button"
                onClick={() => { setShowRecovery(true); setTotpCode(""); }}
                className="mt-1 text-xs text-indigo-700 hover:text-indigo-900"
              >
                Use recovery code instead
              </button>
            </div>
          )}

          {showRecovery && (
            <div>
              <label htmlFor="recovery" className="block text-sm font-medium text-slate-700 mb-1">
                Recovery code
              </label>
              <input
                id="recovery"
                type="text"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value)}
                autoComplete="one-time-code"
                placeholder="XXXX-XXXX-XXXX"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                type="button"
                onClick={() => { setShowRecovery(false); setRecoveryCode(""); }}
                className="mt-1 text-xs text-indigo-700 hover:text-indigo-900"
              >
                Use TOTP code instead
              </button>
            </div>
          )}

          {error && (
            <p role="alert" className="text-sm text-red-600">{error}</p>
          )}
          {info && (
            <p role="status" className="text-sm text-emerald-700">{info}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 rounded-lg bg-indigo-700 text-white font-medium text-sm hover:bg-indigo-800 disabled:opacity-50"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>

          <button
            type="button"
            disabled={resetSending}
            onClick={() => { void handleForgotPassword(); }}
            className="w-full text-sm text-indigo-700 hover:text-indigo-900 disabled:opacity-50"
          >
            {resetSending ? "Sending…" : "Forgot password?"}
          </button>
        </form>
      </div>
    </div>
  );
}
